// file: /src/Pages/NewReservation/StepOne/algorithm/maxArrivalsFilter.js

/**
 * Simple max arrivals filter for time slots
 * 
 * Only considers exact arrivals at each time slot without factoring duration
 */

/**
 * Get meal type based on time
 * @param {string} time - Time string (HH:MM)
 * @returns {string|null} - Meal type or null
 */
function getMealType(time) {
	const hour = parseInt(time.split(':')[0], 10);
	
	if (hour >= 4 && hour < 11) return 'breakfast';
	if (hour >= 11 && hour < 16) return 'lunch';
	if (hour >= 16 && hour < 23) return 'dinner';
	
	return null;
  }
  
  /**
   * Extract number value from various data formats
   * @param {*} value - Value from data object
   * @returns {number|null} - Number or null
   */
  function extractNumber(value) {
	if (!value) return null;
	
	// Handle MongoDB NumberInt format
	if (value.$numberInt) {
	  return parseInt(value.$numberInt, 10);
	}
	
	// Handle regular number
	if (typeof value === 'number') {
	  return value;
	}
	
	// Handle string number
	if (typeof value === 'string') {
	  const parsed = parseInt(value, 10);
	  return isNaN(parsed) ? null : parsed;
	}
	
	return null;
  }
  
  /**
   * Count guests arriving at exact time
   * @param {Array} reservations - Reservation list
   * @param {string} date - Date (YYYY-MM-DD)
   * @param {string} time - Time (HH:MM)
   * @returns {number} - Guest count
   */
  function countArrivalsAtTime(reservations, date, time) {
	return reservations
	  .filter(r => r.date === date && r.time === time)
	  .reduce((sum, r) => sum + (parseInt(r.guests, 10) || 0), 0);
  }
  
  /**
   * Filter timeblocks based on max arrivals settings
   * @param {Object} restaurantData - Restaurant data
   * @param {string} date - Date string
   * @param {Object} timeblocks - Available timeblocks
   * @param {Array} reservations - Existing reservations
   * @param {number} guests - New reservation guest count
   * @returns {Object} - Filtered timeblocks
   */
  function filterTimeblocksByMaxArrivals(restaurantData, date, timeblocks, reservations, guests) {
	const filteredBlocks = {};
	
	for (const [time, timeData] of Object.entries(timeblocks)) {
	  // Get meal type for this time
	  const mealType = getMealType(time);
	  if (!mealType) {
		// Keep the timeblock if we can't determine its meal type
		filteredBlocks[time] = timeData;
		continue;
	  }
	  
	  // Get max arrivals config for this meal type
	  const maxArrivalsConfig = restaurantData[`max-arrivals-${mealType}`];
	  if (!maxArrivalsConfig) {
		// Keep the timeblock if no max arrivals config for this meal type
		filteredBlocks[time] = timeData;
		continue; 
	  }
	  
	  // Get max arrivals value for this specific time
	  const maxArrivals = extractNumber(maxArrivalsConfig[time]);
	  if (maxArrivals === null) {
		// Keep the timeblock if no specific max arrivals for this time
		filteredBlocks[time] = timeData;
		continue;
	  }
	  
	  // Count current arrivals at this exact time
	  const currentArrivals = countArrivalsAtTime(reservations, date, time);
	  
	  // Only include timeblock if adding these guests doesn't exceed max arrivals
	  if (currentArrivals + guests <= maxArrivals) {
		filteredBlocks[time] = timeData;
	  }
	}
	
	return filteredBlocks;
  }
  
  module.exports = {
	filterTimeblocksByMaxArrivals
  };